import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type MutableRefObject,
} from 'react';

import {
  cardBlurDisengages,
  cardFocusEngages,
  pointerDownDismisses,
} from './collapsedEngagement';

export interface UseCollapsedEngagementArgs {
  isCollapsible: boolean;
  hasContent: boolean;
  autoFocus: boolean;
  disabled: boolean;
  isSubmitting: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export interface CollapsedEngagement {
  showToolbar: boolean;
  showInlineControls: boolean;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  cardRef: MutableRefObject<HTMLDivElement | null>;
  cardHandlers: {
    onFocusCapture: (event: FocusEvent<HTMLDivElement>) => void;
    onBlur: (event: FocusEvent<HTMLDivElement>) => void;
  };
  engage: () => void;
  disengage: () => void;
}

export const useCollapsedEngagement = ({
  isCollapsible,
  hasContent,
  autoFocus,
  disabled,
  isSubmitting,
  onExpandedChange,
}: UseCollapsedEngagementArgs): CollapsedEngagement => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);
  // Compact composer stays a calm single row until the clinician engages with
  // it — focusing, typing, staging a file, or linking context. Only then does
  // it reveal the full toolbar (mode toggle, approval mode, send).
  const [isEngaged, setIsEngaged] = useState(
    () => autoFocus && !disabled && !isSubmitting
  );

  // `collapsed` starts as a single row and expands once the clinician engages
  // (focus/content/attachments); `full` is expanded throughout.
  const isExpanded = isEngaged || hasContent;
  const showToolbar = !isCollapsible || isExpanded;
  const showInlineControls = isCollapsible && !isExpanded;

  const expandedSignal = isCollapsible && isExpanded;
  const lastExpandedRef = useRef<boolean | null>(null);
  useEffect(() => {
    if (!isCollapsible) return;
    if (lastExpandedRef.current === expandedSignal) return;
    lastExpandedRef.current = expandedSignal;
    onExpandedChange?.(expandedSignal);
  }, [expandedSignal, isCollapsible, onExpandedChange]);

  useEffect(() => {
    if (!isCollapsible || !isEngaged) return;
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      const insideContainer = containerRef.current?.contains(target ?? null) ?? false;
      if (!pointerDownDismisses(target, insideContainer)) return;
      setIsEngaged(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [isCollapsible, isEngaged]);

  // Track focus at the card level (not the editor) so clicking a toolbar
  // control — dictate, the mode pill, the send button — keeps the compact
  // composer expanded instead of collapsing the moment the editor blurs.
  const onFocusCapture = (event: FocusEvent<HTMLDivElement>) => {
    if (!isCollapsible) return;
    if (cardFocusEngages(event.target)) setIsEngaged(true);
  };

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!isCollapsible) return;
    const nextFocused = event.relatedTarget;
    const insideCard =
      nextFocused instanceof Node && (cardRef.current?.contains(nextFocused) ?? false);
    if (cardBlurDisengages(nextFocused, insideCard)) setIsEngaged(false);
  };

  const engage = useCallback(() => setIsEngaged(true), []);
  // The counterpart to `engage`, for a surface that settles the composer
  // deliberately rather than by moving focus — a dismiss on one of the
  // `data-compact-composer-interaction` panels, which the pointer and blur
  // guards above both hold engagement through on purpose.
  const disengage = useCallback(() => setIsEngaged(false), []);

  return {
    showToolbar,
    showInlineControls,
    containerRef,
    cardRef,
    cardHandlers: { onFocusCapture, onBlur },
    engage,
    disengage,
  };
};
